import qz from 'qz-tray';
import { useEffect, useState } from 'react';
import api from '../../services/api';
import Select from '../ui/Select';
import { ExtractListRows } from './payload';

/*****************************
 * Impressão de ZPL — fluxo ÚNICO do módulo Armazém
 *****************************
 * Nasceu dentro do LocationLabelsModal (M1) e foi EXTRAÍDO aqui no M3: a
 * etiqueta de LPN usa exatamente o mesmo caminho da etiqueta de endereço, e
 * duplicar o seletor de impressora era garantir divergência (CLAUDE.md §4).
 * Quem imprime ZPL no módulo usa este arquivo — não reimplemente.
 *
 * O seletor unificado tem DOIS grupos:
 *  - REDE (cadastradas): impressoras de `/warehouse/printers` ativas (host:porta).
 *    O ZPL vai pelo QZ Tray DESTE computador direto para host:porta na rede
 *    local — o servidor do Melidash fica na nuvem e não alcança a rede do
 *    armazém; ele só guarda o cadastro e assina os pedidos ao QZ. Testar e
 *    Detectar também saem daqui (`TestNetworkPrinterViaQz`,
 *    `DiscoverNetworkPrintersViaQz`).
 *  - USB desta máquina (QZ Tray): impressoras LOCAIS do computador de quem
 *    está imprimindo, descobertas na hora por qz.printers.find() — sem
 *    cadastro, porque USB é pessoal do terminal. O ZPL vai DIRETO ao QZ, nunca
 *    passa pelo backend. QZ fora do ar → o grupo vira item desabilitado com a
 *    instrução.
 *
 * SEM ASSINATURA DIGITAL (não configuramos qz.security.*): o próprio QZ Tray
 * abre o prompt "Allow/Block" na primeira ação de cada conexão — decisão do
 * dono na 4ª rodada do M1. A conexão é reaproveitada entre impressões (1
 * prompt por sessão do QZ, não por etiqueta).
 */

// Porta default de impressora de etiqueta (só exibição; o backend aplica a dele).
const DEFAULT_PRINTER_PORT = 9100;

// Valores do select unificado (rede cadastrada × USB local).
/**
 * A impressora do NAVEGADOR, uma só para todo o MELIDASH: a bancada a define
 * logo depois do crachá e toda tela que imprime (bancadas, etiquetas de LPN e
 * de endereço, coletas…) usa essa. Trocar em qualquer lugar troca em todos.
 * (O nome da chave ficou do tempo em que só a bancada lembrava.)
 */
const PRINTER_STORAGE_KEY = 'wms.bancada.impressora';
// Avisa as outras telas abertas NESTA aba (o evento `storage` só chega nas
// outras abas).
const PRINTER_CHANGED_EVENT = 'melidash:impressora';

function readStoredPrinter() {
    try {
        return localStorage.getItem(PRINTER_STORAGE_KEY) || '';
    } catch {
        return '';
    }
}

export const NETWORK_VALUE_PREFIX = 'net:';
export const USB_VALUE_PREFIX = 'usb:';

/*
 * ASSINATURA — o que faz o QZ parar de perguntar "Allow" a cada etiqueta.
 *
 * O QZ só confia num site que ASSINE as chamadas com a chave par do
 * certificado instalado no Site Manager daquela máquina. O certificado vem do
 * backend; a assinatura também, porque a chave privada não desce para o
 * navegador do chão de fábrica.
 *
 * Configurar isto é obrigatório ANTES de conectar. Se o ambiente não tiver o
 * par (dev de alguém, servidor sem a chave), segue sem assinar: imprime igual,
 * só volta a pedir a confirmação — nunca deixar a bancada sem imprimir por
 * causa disso.
 */
let promessaDeSeguranca = null;

function ConfigurarAssinaturaDoQz() {
    if (!promessaDeSeguranca) {
        promessaDeSeguranca = (async () => {
            let certificado = '';
            try {
                certificado = await api.getQzCertificate();
            } catch {
                return false;
            }
            if (!certificado || !certificado.includes('BEGIN CERTIFICATE')) {
                return false;
            }
            try {
                qz.security.setCertificatePromise((resolve) => resolve(certificado));
                // SHA512 é o padrão do QZ 2.1+ e é o que o backend assina. Mudar
                // de um lado só quebra a assinatura em silêncio — o QZ volta a
                // pedir Allow sem dizer por quê.
                qz.security.setSignatureAlgorithm('SHA512');
                qz.security.setSignaturePromise((desafio) => async (resolve, reject) => {
                    try {
                        const resposta = await api.postQzSign(desafio);
                        resolve(resposta?.assinatura ?? resposta?.data?.assinatura ?? '');
                    } catch (erro) {
                        reject(erro);
                    }
                });
            } catch {
                // Versão do QZ sem essa API, ou qualquer outro tropeço na
                // configuração: seguir SEM assinar. Volta a pedir Allow, que é
                // chato — mas bancada parada por causa de conforto é pior.
                return false;
            }
            return true;
        })();
    }
    return promessaDeSeguranca;
}

// A tentativa de conexão em andamento. O `isActive()` do QZ diz "sim" também
// ENQUANTO conecta (readyState CONNECTING): sem esperar a tentativa, uma
// segunda chamada seguia com o socket ainda fechado — o Detectar "varria" a
// rede sem QZ e dizia "nenhuma impressora" em vez de "QZ fora do ar".
let conexaoEmAndamento = null;

async function ensureQzConnected() {
    if (conexaoEmAndamento) {
        // Espera a tentativa de outra chamada; se ela falhou, o QZ acabou de
        // provar que não está aqui — devolve a mesma falha (o próximo clique
        // tenta de novo) em vez de dobrar a espera do operador.
        await conexaoEmAndamento;
        return;
    }
    if (qz.websocket.isActive()) {
        return;
    }
    const tentativa = (async () => {
        // Antes de conectar, sempre: depois de aberto o QZ já decidiu se confia.
        await ConfigurarAssinaturaDoQz();
        await qz.websocket.connect();
    })();
    conexaoEmAndamento = tentativa;
    try {
        await tentativa;
    } catch (error) {
        // Corrida benigna: outra aba/chamada conectou primeiro.
        if (qz.websocket.isActive()) {
            return;
        }
        throw error;
    } finally {
        if (conexaoEmAndamento === tentativa) {
            conexaoEmAndamento = null;
        }
    }
}

/** Lista os nomes das impressoras LOCAIS (SO) via QZ Tray. */
async function listLocalPrinters() {
    await ensureQzConnected();
    const found = await qz.printers.find();
    if (Array.isArray(found)) {
        return found.map((name) => String(name)).filter(Boolean);
    }
    return found ? [String(found)] : [];
}

/** Uma promessa com prazo: o QZ não tem timeout próprio para abrir socket. */
function withTimeout(promise, ms) {
    let timer;
    return Promise.race([
        promise,
        new Promise((_, reject) => {
            timer = setTimeout(() => reject(new Error('timeout')), ms);
        }),
    ]).finally(() => clearTimeout(timer));
}

// Porta RAW/ZPL padrão das impressoras de etiqueta.
const NETWORK_PRINTER_PORT = DEFAULT_PRINTER_PORT;
// Prazo para uma impressora de rede responder ao Testar/Detectar.
const NETWORK_PROBE_TIMEOUT_MS = 2500;
// Quantas tentativas o Detectar faz ao mesmo tempo (/24 = 254 endereços).
const NETWORK_SCAN_CONCURRENCY = 24;

function networkTarget(printer) {
    const port = Number(printer?.port) || NETWORK_PRINTER_PORT;
    return { host: String(printer?.host ?? '').trim(), port };
}

/**
 * ZPL cru para uma impressora de REDE (host:porta) pelo QZ Tray deste
 * computador: navegador → QZ (localhost) → TCP na rede local → impressora.
 */
async function printZplViaQzHost(printer, zpl, label) {
    try {
        await ensureQzConnected();
    } catch {
        throw new Error(label('wms.labels.qz_not_running'));
    }
    const target = networkTarget(printer);
    if (!target.host) {
        throw new Error(label('wms.printer.no_host'));
    }
    const config = qz.configs.create(target);
    await qz.print(config, [{ type: 'raw', format: 'command', flavor: 'plain', data: zpl }]);
}

/**
 * TESTAR uma impressora de rede pelo QZ deste computador: abre e fecha uma
 * conexão TCP com host:porta (não imprime). `true` = respondeu. QZ fora do ar
 * é erro (a mensagem diz para abrir o QZ), não "sem resposta".
 */
export async function TestNetworkPrinterViaQz(printer, label) {
    try {
        await ensureQzConnected();
    } catch {
        throw new Error(label('wms.labels.qz_not_running'));
    }
    const { host, port } = networkTarget(printer);
    if (!host) {
        return false;
    }
    try {
        await withTimeout(qz.socket.open(host, port), NETWORK_PROBE_TIMEOUT_MS);
    } catch {
        // abriu tarde demais? fecha para não deixar conexão pendurada
        qz.socket.close(host, port).catch(() => {});
        return false;
    }
    await qz.socket.close(host, port).catch(() => {});
    return true;
}

/** Os 3 primeiros octetos da rede DESTE computador (pelo QZ), ou null. */
export async function DetectLocalNetworkBase() {
    try {
        await ensureQzConnected();
        const device = await qz.networking.device();
        const match = /^(\d{1,3}\.\d{1,3}\.\d{1,3})\.\d{1,3}$/.exec(String(device?.ip ?? ''));
        return match ? match[1] : null;
    } catch {
        return null;
    }
}

/**
 * DETECTAR impressoras de rede pelo QZ deste computador: tenta a porta 9100
 * em cada endereço da /24 (`baseIp` = 3 octetos) e devolve os que responderam.
 * Nada passa pelo servidor.
 */
export async function DiscoverNetworkPrintersViaQz(baseIp, label, { port = NETWORK_PRINTER_PORT } = {}) {
    try {
        await ensureQzConnected();
    } catch {
        throw new Error(label('wms.labels.qz_not_running'));
    }
    const hosts = Array.from({ length: 254 }, (_, index) => `${baseIp}.${index + 1}`);
    const found = [];
    let next = 0;
    const worker = async () => {
        while (next < hosts.length) {
            const host = hosts[next];
            next += 1;
            if (await TestNetworkPrinterViaQz({ host, port }, label)) {
                found.push({ host, port });
            }
        }
    };
    await Promise.all(Array.from({ length: NETWORK_SCAN_CONCURRENCY }, worker));
    return found.sort((a, b) => Number(a.host.split('.')[3]) - Number(b.host.split('.')[3]));
}

/** ZPL cru direto na impressora local (nome exato vindo do próprio QZ). */
async function printZplViaQzTray(printerName, zpl, label) {
    try {
        await ensureQzConnected();
    } catch {
        throw new Error(label('wms.labels.qz_not_running'));
    }
    const config = qz.configs.create(printerName);
    await qz.print(config, [{ type: 'raw', format: 'plain', data: zpl }]);
}

/**
 * Carrega as impressoras disponíveis enquanto `isActive` (modal aberto, painel
 * em modo impressão). Recarrega a cada ativação: o fluxo natural é cadastrar a
 * impressora e voltar aqui sem F5.
 *
 * `printers`: null = carregando; [] = nenhuma de rede ativa cadastrada.
 * `localPrinters`: null = procurando; {ok:false} = QZ fora do ar.
 */
export function useZplPrinters(isActive) {
    const [printers, setPrinters] = useState(null);
    const [localPrinters, setLocalPrinters] = useState(null);
    // A impressora é do POSTO, não da pessoa: fica guardada no navegador da
    // bancada e sobrevive à troca de crachá e ao recarregar a página. Sem isso
    // cada operador que assume teria de escolher de novo, no meio do turno.
    const [selectedPrinter, setSelectedPrinter] = useState(readStoredPrinter);

    // O que está na tela é o que fica guardado — inclusive quando quem muda a
    // escolha é o próprio hook (preferência apontando para impressora
    // descadastrada). Um ponto só de gravação, sem caminho que escape; e só
    // avisa as outras telas quando o valor guardado MUDOU (senão uma acorda a
    // outra sem fim).
    useEffect(() => {
        if (readStoredPrinter() === selectedPrinter) {
            return;
        }
        try {
            if (selectedPrinter) {
                localStorage.setItem(PRINTER_STORAGE_KEY, selectedPrinter);
            } else {
                localStorage.removeItem(PRINTER_STORAGE_KEY);
            }
        } catch {
            // navegador sem storage: a escolha vale para esta sessão
            return;
        }
        window.dispatchEvent(new CustomEvent(PRINTER_CHANGED_EVENT));
    }, [selectedPrinter]);

    // A impressora é uma só: quando outra tela (ou outra aba) troca, esta segue.
    useEffect(() => {
        const follow = (event) => {
            if (event.type === 'storage' && event.key !== PRINTER_STORAGE_KEY) {
                return;
            }
            setSelectedPrinter(readStoredPrinter());
        };
        window.addEventListener(PRINTER_CHANGED_EVENT, follow);
        window.addEventListener('storage', follow);
        return () => {
            window.removeEventListener(PRINTER_CHANGED_EVENT, follow);
            window.removeEventListener('storage', follow);
        };
    }, []);

    useEffect(() => {
        if (!isActive) {
            return undefined;
        }
        let alive = true;
        // NÃO zerar a seleção aqui: este efeito roda a cada vez que a tela
        // monta, e zerar fazia a escolha do operador (feita na tela do crachá)
        // sumir ao entrar na bancada — a etiqueta então "não ia a lugar
        // nenhum". O que estava guardado no posto vale até ele trocar.
        setPrinters(null);
        api.getWarehousePrinters({ active: 'true', limit: 500 })
            .then((data) => {
                if (!alive) {
                    return;
                }
                const rows = ExtractListRows(data, ['printers']);
                setPrinters(rows);
                // Só escolhe a primeira de rede quando o posto AINDA não tem
                // preferência — nunca por cima do que o operador escolheu.
                // A exceção é a preferência PODRE: se o posto lembra uma
                // impressora de rede que foi descadastrada, insistir nela é
                // mandar etiqueta para um id morto. USB não dá para conferir
                // assim (o QZ pode estar fora do ar num momento e voltar no
                // outro), então essa fica intocada.
                const primeiraDeRede = rows[0]?.id ? `${NETWORK_VALUE_PREFIX}${rows[0].id}` : '';
                setSelectedPrinter((previous) => {
                    if (!previous) {
                        return primeiraDeRede;
                    }
                    if (!previous.startsWith(NETWORK_VALUE_PREFIX)) {
                        return previous;
                    }
                    const id = previous.slice(NETWORK_VALUE_PREFIX.length);
                    const aindaExiste = rows.some((row) => String(row.id) === id);
                    return aindaExiste ? previous : primeiraDeRede;
                });
            })
            .catch(() => {
                if (alive) {
                    setPrinters([]);
                }
            });

        setLocalPrinters(null);
        listLocalPrinters()
            .then((names) => {
                if (alive) {
                    setLocalPrinters({ ok: true, names });
                }
            })
            .catch(() => {
                if (alive) {
                    setLocalPrinters({ ok: false, names: [] });
                }
            });

        return () => {
            alive = false;
        };
    }, [isActive]);

    return { printers, localPrinters, selectedPrinter, setSelectedPrinter };
}

/** Select unificado rede × USB + o aviso de "nenhuma impressora de rede". */
export const ZplPrinterSelect = ({ label, printers, localPrinters, value, onChange, disabled = false }) => {
    const networkPrinters = printers ?? [];
    return (
        <div className="space-y-2">
            <Select
                label={label('wms.labels.printer')}
                value={value}
                onChange={(event) => onChange(event.target.value)}
                disabled={disabled || printers === null}
            >
                {!value ? <option value="">{label('wms.labels.select_printer')}</option> : null}
                <optgroup label={label('wms.labels.network_group')}>
                    {printers === null ? (
                        <option value="" disabled>
                            …
                        </option>
                    ) : networkPrinters.length === 0 ? (
                        <option value="" disabled>
                            {label('wms.labels.network_none')}
                        </option>
                    ) : (
                        networkPrinters.map((printer) => (
                            <option key={printer.id} value={`${NETWORK_VALUE_PREFIX}${printer.id}`}>
                                {`${printer.label} (${printer.host}:${printer.port ?? DEFAULT_PRINTER_PORT})`}
                            </option>
                        ))
                    )}
                </optgroup>
                <optgroup label={label('wms.labels.usb_group')}>
                    {localPrinters === null ? (
                        <option value="" disabled>
                            {label('wms.labels.usb_loading')}
                        </option>
                    ) : !localPrinters.ok ? (
                        <option value="" disabled>
                            {label('wms.labels.qz_not_running')}
                        </option>
                    ) : localPrinters.names.length === 0 ? (
                        <option value="" disabled>
                            {label('wms.labels.usb_none')}
                        </option>
                    ) : (
                        localPrinters.names.map((name) => (
                            <option key={name} value={`${USB_VALUE_PREFIX}${name}`}>
                                {name}
                            </option>
                        ))
                    )}
                </optgroup>
            </Select>
            {printers !== null && networkPrinters.length === 0 ? (
                <p className="text-xs text-mono-500 dark:text-mono-400">{label('wms.labels.no_printers')}</p>
            ) : null}
        </div>
    );
};

/**
 * O nome exibível da impressora escolhida (rede: o rótulo do cadastro; USB: o
 * nome do QZ). '' quando não há escolha; o próprio id quando a lista de rede
 * ainda não carregou.
 */
export function DescribePrinter(target, printers) {
    const value = String(target || '');
    if (!value) {
        return '';
    }
    if (value.startsWith(USB_VALUE_PREFIX)) {
        return value.slice(USB_VALUE_PREFIX.length);
    }
    const printerId = value.slice(NETWORK_VALUE_PREFIX.length);
    const printer = (printers ?? []).find((row) => String(row.id) === printerId);
    return printer?.label || '…';
}

/**
 * Envia o ZPL para o alvo escolhido no select. Devolve o NOME exibível da
 * impressora (para o toast); erros sobem para quem chamou tratar.
 */
export async function SendZplToPrinter({ target, printers, zpl, label }) {
    if (String(target).startsWith(USB_VALUE_PREFIX)) {
        // USB nunca passa pelo backend: o ZPL vai direto ao QZ Tray da máquina
        // local (nome exato listado pelo próprio QZ).
        const printerName = String(target).slice(USB_VALUE_PREFIX.length);
        await printZplViaQzTray(printerName, zpl, label);
        return printerName;
    }
    // REDE: pelo QZ deste computador para host:porta (o servidor não alcança a
    // rede do armazém).
    const printerId = String(target).slice(NETWORK_VALUE_PREFIX.length);
    const printer = (printers ?? []).find((row) => String(row.id) === printerId);
    if (!printer) {
        throw new Error(label('wms.printer.not_loaded'));
    }
    await printZplViaQzHost(printer, zpl, label);
    return printer.label || '';
}

/** Baixa o ZPL como arquivo (fallback quando não há impressora à mão). */
export function DownloadZplFile(zpl, fileName) {
    const blob = new Blob([zpl], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
}
